// ============================================================================
// SearchPalette — palette de recherche globale Ctrl+K (GEST-SEARCH-GLOBAL)
// ============================================================================
// Overlay (PAS une route SPA). GsSearchInput en tete, resultats groupes
// personne > equipe > affichage. Clavier : fleches inter-groupes, Enter
// navigue+ferme, Escape, focus-trap, role=dialog aria-modal + listbox /
// aria-activedescendant. Candidat DS (1 seul consommateur — pas @gs/ui).

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { EmptyState, GsSearchInput, SkeletonLine } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { useGlobalSearch, type SearchResult, type SearchResultType } from '../../hooks/useGlobalSearch';

const DEBOUNCE_MS = 250;
const GROUP_ORDER: SearchResultType[] = ['person', 'team', 'posting'];

export interface SearchPaletteProps {
  open: boolean;
  onClose: () => void;
}

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function localizeSublabel(sublabel: string | null, t: (key: string) => string): string | null {
  if (!sublabel) return null;
  if (sublabel.endsWith(' · open')) {
    return `${sublabel.slice(0, -' · open'.length)} · ${t('gest:search.status.open')}`;
  }
  if (sublabel.endsWith(' · filled')) {
    return `${sublabel.slice(0, -' · filled'.length)} · ${t('gest:search.status.filled')}`;
  }
  if (sublabel.endsWith(' · cancelled')) {
    return `${sublabel.slice(0, -' · cancelled'.length)} · ${t('gest:search.status.cancelled')}`;
  }
  return sublabel;
}

function groupLabel(type: SearchResultType, t: (key: 'gest:search.groups.person' | 'gest:search.groups.team' | 'gest:search.groups.posting') => string): string {
  if (type === 'person') return t('gest:search.groups.person');
  if (type === 'team') return t('gest:search.groups.team');
  return t('gest:search.groups.posting');
}

export function SearchPalette({ open, onClose }: SearchPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const titleId = useId();
  const listboxId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const inputWrapRef = useRef<HTMLDivElement>(null);

  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q, DEBOUNCE_MS);
  const query = useGlobalSearch(debouncedQ);
  const [activeIndex, setActiveIndex] = useState(0);

  const results = query.data?.results ?? [];
  const flat = results;

  const groups = useMemo(() => {
    return GROUP_ORDER
      .map((type) => ({
        type,
        items: flat.filter((r) => r.type === type),
      }))
      .filter((g) => g.items.length > 0);
  }, [flat]);

  // Reset a l'ouverture / fermeture.
  useEffect(() => {
    if (!open) return;
    setQ('');
    setActiveIndex(0);
    // Focus le champ apres paint.
    const id = window.requestAnimationFrame(() => {
      const input = inputWrapRef.current?.querySelector('input');
      input?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  // Clamp activeIndex quand la liste change.
  useEffect(() => {
    if (activeIndex >= flat.length) setActiveIndex(Math.max(0, flat.length - 1));
  }, [flat.length, activeIndex]);

  const go = useCallback((item: SearchResult) => {
    onClose();
    navigate(item.href);
  }, [navigate, onClose]);

  // Focus-trap + Escape + fleches (sur le panneau).
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === 'ArrowDown') {
        if (flat.length === 0) return;
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % flat.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        if (flat.length === 0) return;
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
        return;
      }
      if (e.key === 'Enter') {
        const item = flat[activeIndex];
        if (!item) return;
        e.preventDefault();
        go(item);
        return;
      }

      // Focus-trap Tab dans le panneau.
      if (e.key === 'Tab' && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flat, activeIndex, go, onClose]);

  if (!open) return null;

  const trimmed = q.trim();
  const showHint = trimmed.length < 2;
  const showSkeleton = !showHint && query.isFetching && !query.data;
  const showEmpty = !showHint && !query.isFetching && query.isSuccess && flat.length === 0;
  const showError = !showHint && query.isError;
  const activeId = flat[activeIndex] ? `${listboxId}-opt-${activeIndex}` : undefined;

  let flatOffset = 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-3 pt-16 sm:pt-24">
      <button
        type="button"
        aria-label={t('gest:search.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/40 dark:bg-black/60"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-lg overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800"
      >
        <h2 id={titleId} className="sr-only">{t('gest:search.title')}</h2>
        <div ref={inputWrapRef} className="border-b border-slate-200 p-3 dark:border-slate-600">
          <GsSearchInput
            value={q}
            onChange={setQ}
            placeholder={t('gest:search.placeholder')}
            autoFocus
            className="w-full"
          />
        </div>

        <div
          id={listboxId}
          role="listbox"
          aria-label={t('gest:search.results')}
          aria-activedescendant={activeId}
          className="max-h-80 overflow-y-auto p-2"
        >
          {showHint && (
            <p className="px-3 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('gest:search.hint')}
            </p>
          )}

          {showSkeleton && (
            <div className="space-y-2 px-2 py-3" aria-busy="true" aria-label={t('gest:search.loading')}>
              <SkeletonLine className="h-4 w-24" />
              <SkeletonLine className="h-10 w-full" />
              <SkeletonLine className="h-10 w-full" />
              <SkeletonLine className="h-10 w-5/6" />
            </div>
          )}

          {showError && (
            <div className="px-3 py-6 text-center">
              <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">{t('gest:search.error')}</p>
              <button
                type="button"
                onClick={() => void query.refetch()}
                className="touch-manipulation rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
              >
                {t('gest:search.retry')}
              </button>
            </div>
          )}

          {showEmpty && (
            <EmptyState
              title={t('gest:search.emptyTitle')}
              description={t('gest:search.emptyDescription', { q: debouncedQ })}
            />
          )}

          {!showHint && !showSkeleton && !showError && groups.map((group) => {
            const start = flatOffset;
            flatOffset += group.items.length;
            return (
              <div key={group.type} className="mb-2">
                <div className="px-2 py-1 text-chip font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  {groupLabel(group.type, t)}
                </div>
                <ul className="list-none p-0">
                  {group.items.map((item, i) => {
                    const index = start + i;
                    const selected = index === activeIndex;
                    const optionId = `${listboxId}-opt-${index}`;
                    return (
                      <li key={`${item.type}-${item.id}`} role="presentation">
                        <button
                          type="button"
                          id={optionId}
                          role="option"
                          aria-selected={selected}
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => go(item)}
                          className={[
                            'flex w-full touch-manipulation flex-col items-start rounded-lg px-3 py-2 text-left transition-colors',
                            selected
                              ? 'bg-brand-50 text-slate-900 dark:bg-brand-950 dark:text-slate-100'
                              : 'text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-700',
                          ].join(' ')}
                        >
                          <span className="text-sm font-medium">{item.label}</span>
                          {item.sublabel && (
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {localizeSublabel(item.sublabel, t)}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Declencheur loupe — gabarit cloche/bulle (trailingSlot header). */
export function SearchTriggerButton({ onOpen, label }: { onOpen: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      className="touch-manipulation rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
    >
      <Search size={15} aria-hidden />
    </button>
  );
}
